import React from "react";
import { PrimaryButton, Stack, Text, Label } from "@fluentui/react";
import type { DocumentClassificationFeedbackDto } from "@/api/feedback";

interface Props {
  feedback: DocumentClassificationFeedbackDto;
  onReview?: () => void;
  onClose?: () => void;
}

const formatDate = (iso: string) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
};

const labelStyle: React.CSSProperties = {
  fontWeight: 600,
  color: "#323130",
  minWidth: 140,
};

const valueStyle: React.CSSProperties = {
  color: "#605e5c",
  wordBreak: "break-word",
};

const OcrFeedbackDetailsPanel: React.FC<Props> = ({ feedback, onReview }) => {
  const parseFeedbackData = (): Record<string, string> | null => {
    try {
      const parsed = JSON.parse(feedback.feedbackRequest);
      return typeof parsed === "object" && parsed !== null ? parsed : null;
    } catch {
      return null;
    }
  };

  const feedbackData = parseFeedbackData();

  return (
    <Stack tokens={{ childrenGap: 16 }} styles={{ root: { padding: "8px 0" } }}>
      {/* ── Summary ── */}
      <Stack tokens={{ childrenGap: 8 }}>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <Text style={labelStyle}>ID:</Text>
          <Text style={valueStyle}>{feedback.id}</Text>
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <Text style={labelStyle}>Job ID:</Text>
          <Text style={valueStyle}>{feedback.classificationJobId}</Text>
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <Text style={labelStyle}>Success:</Text>
          <Text
            style={{
              fontWeight: 600,
              color: feedback.success ? "#107c10" : "#d13438",
            }}
          >
            {feedback.success ? "Yes" : "No"}
          </Text>
        </Stack>
        {feedback.error && (
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <Text style={labelStyle}>Error:</Text>
            <Text style={{ ...valueStyle, color: "#d13438" }}>{feedback.error}</Text>
          </Stack>
        )}
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <Text style={labelStyle}>Created:</Text>
          <Text style={valueStyle}>{formatDate(feedback.createdAtUtc)}</Text>
        </Stack>
      </Stack>

      {/* ── Feedback Data ── */}
      <div style={{ height: 1, backgroundColor: "#e1e5e9", margin: "4px 0" }} />
      <Label styles={{ root: { fontSize: 16, fontWeight: 600 } }}>Feedback Data</Label>

      {feedbackData ? (
        <Stack tokens={{ childrenGap: 8 }}>
          {Object.entries(feedbackData).map(([key, value]) => (
            <Stack
              key={key}
              horizontal
              tokens={{ childrenGap: 8 }}
              styles={{
                root: {
                  padding: "6px 10px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: 4,
                },
              }}
            >
              <Text style={{ ...labelStyle, minWidth: 180 }}>{key}:</Text>
              <Text style={valueStyle}>{value}</Text>
            </Stack>
          ))}
        </Stack>
      ) : (
        <Text style={valueStyle}>{feedback.feedbackRequest || "—"}</Text>
      )}

      {/* ── Response ── */}
      {feedback.feedbackResponse && (
        <>
          <div style={{ height: 1, backgroundColor: "#e1e5e9", margin: "4px 0" }} />
          <Label styles={{ root: { fontSize: 16, fontWeight: 600 } }}>Response</Label>
          <Text
            styles={{
              root: {
                padding: "6px 10px",
                backgroundColor: "#f8f9fa",
                borderRadius: 4,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "monospace",
                fontSize: 13,
              },
            }}
          >
            {feedback.feedbackResponse}
          </Text>
        </>
      )}

      {/* ── Mark as Reviewed ── */}
      {feedback.isActive && onReview && (
        <>
          <div style={{ height: 1, backgroundColor: "#e1e5e9", margin: "4px 0" }} />
          <PrimaryButton
            text="Mark as Reviewed"
            iconProps={{ iconName: "CheckMark" }}
            onClick={onReview}
          />
        </>
      )}
    </Stack>
  );
};

export default OcrFeedbackDetailsPanel;
